import {
  ClearRounded as ClearIcon,
  EditRounded as EditIcon,
  DeleteRounded as DeleteIcon,
} from "@mui/icons-material";
import {
  Card,
  ClickAwayListener,
  Grid,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import IconButton from "components/common/IconButton";
import SubmissionLink from "components/common/SubmissionLink";
import SubmissionSelector from "components/common/SubmissionSearch";
import SubmissionSummary from "components/submission/SubmissionSummary";
import * as labels from "config.labels";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "store/actions/input/program/programActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";

const PriorSubmissionCard = (props) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const priorSubmissionFilename = useSelector(
    programSelectors.getPriorSubmission
  );

  const hasPriorSubmission = !!priorSubmissionFilename;

  const priorProgram = useSelector(
    (state) => state.temp?.priorState?.state?.input?.program
  );

  const dispatch = useDispatch();
  const updatePriorSubmission = useCallback(
    (priorSubmissionFilename) => {
      dispatch(actions.updatePriorSubmission(priorSubmissionFilename));
    },
    [dispatch]
  );

  return (
    <Card variant={"outlined"}>
      <Grid container>
        <Grid item container xs={9}>
          {hasPriorSubmission ? (
            <SubmissionSummary program={priorProgram} />
          ) : (
            <Grid item container alignItems={"center"} sx={{ padding: "10px" }}>
              <Typography>
                {translateLabel(labels.RENEWAL_NO_PRIOR_SUBMISSION, {
                  default: "No prior submission",
                })}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Grid
          item
          container
          xs={3}
          alignItems={"center"}
          justifyContent={"flex-end"}
        >
          <Stack direction={"row"} sx={{ marginRight: "0.5rem" }}>
            <Disabled ifReadOnly>
              <IconButton
                variant={"bright"}
                icon={EditIcon}
                onClick={props.onEdit}
              />
            </Disabled>
            {hasPriorSubmission && (
              <Disabled ifReadOnly>
                <IconButton
                  variant={"bright"}
                  icon={DeleteIcon}
                  onClick={() => updatePriorSubmission(null)}
                />
              </Disabled>
            )}
            {hasPriorSubmission && (
              <Disabled ifReadOnly>
                <SubmissionLink
                  variant={"buttonicon"}
                  submissionId={priorSubmissionFilename?.slice(0, -5)}
                />
              </Disabled>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
};

const PriorSubmissionSearch = ({ label, textFieldInputProps, onSelect }) => {
  const filename = useSelector(persistenceSelectors.selectFilename);

  const dispatch = useDispatch();

  return (
    <SubmissionSelector
      label={label}
      textFieldInputProps={textFieldInputProps}
      excludeFilenames={[filename]}
      onSelect={(submission) => {
        dispatch(actions.updatePriorSubmission(submission.filename));
        onSelect();
      }}
    />
  );
};

const RenewalComponent = () => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  return (
    <Component
      title={translateLabel(labels.RENEWAL_COMPONENT_TITLE, {
        default: "Renewed From",
      })}
    >
      {isSearchVisible ? (
        <ClickAwayListener
          onClickAway={() => {
            setIsSearchVisible(false);
          }}
        >
          <div>
            <PriorSubmissionSearch
              label={translateLabel(labels.RENEWAL_SELECT_PRIOR_SUBMISSION, {
                default: "Select prior Submission",
              })}
              onSelect={() => setIsSearchVisible(false)}
              textFieldInputProps={{
                autoFocus: true,
                endAdornment: (
                  <InputAdornment position={"end"}>
                    <IconButton
                      icon={ClearIcon}
                      onClick={() => setIsSearchVisible(false)}
                    />
                  </InputAdornment>
                ),
              }}
              clearOnEscape
            />
          </div>
        </ClickAwayListener>
      ) : (
        <PriorSubmissionCard onEdit={() => setIsSearchVisible(true)} />
      )}
    </Component>
  );
};

export default RenewalComponent;
